import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig, videoPlatforms } from '@/mocks/site';
import { reviews, faqs } from '@/mocks/articles';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: `关于我们 - ${siteConfig.name}`,
        description: `了解${siteConfig.name}的品牌故事、发展历程、用户口碑与联系方式。`,
        url: `${siteConfig.url}/about`,
        isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
      },
      {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        foundingDate: siteConfig.foundingYear,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: siteConfig.phone,
          email: siteConfig.email,
          contactType: 'customer service',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteConfig.address,
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteConfig.name}`}
        description={`了解${siteConfig.name}的品牌故事、发展历程、用户口碑与联系方式。${siteConfig.description}`}
        keywords="青青草视频,关于我们,视频平台介绍,用户口碑,联系方式"
        canonicalUrl={`${siteConfig.url}/about`}
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            <section className="mb-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于{siteConfig.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
                {siteConfig.name}成立于{siteConfig.foundingYear}年，是国内领先的综合视频内容聚合平台。
                我们汇集了妖精视频、桃花视频、苹果视频、榴莲视频等多个主流视频平台的优质资源，
                致力于为用户提供一站式的视频观影体验。
              </p>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                我们的优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {[
                  { icon: 'ri-stack-line', title: '资源丰富', desc: '聚合8大视频平台，海量影视资源一站掌握' },
                  { icon: 'ri-flashlight-line', title: '更新及时', desc: '热门内容同步更新，不会错过任何精彩' },
                  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '严格的内容审核机制，确保观影安全' },
                  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，持续优化平台服务' },
                ].map((item) => (
                  <div key={item.title} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                      <i className={`${item.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                合作平台
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {videoPlatforms.map((p) => (
                  <div key={p.slug} className="p-4 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-gradient-to-br ${p.gradient} mb-3`}>
                      <i className={`${p.icon} text-background-50 text-base`}></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 whitespace-nowrap">{p.name}</h3>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {reviews.map((review) => (
                  <div key={review.id} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`ri-star-fill text-xs ${i < review.rating ? 'text-accent-500' : 'text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                      <span className="ml-auto text-xs text-foreground-500">{review.date}</span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto flex flex-col gap-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between gap-3 p-4 text-left cursor-pointer"
                    >
                      <h3 className="text-sm md:text-base font-medium text-foreground-900">{faq.question}</h3>
                      <i
                        className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-foreground-500 text-lg flex-shrink-0 transition-transform`}
                      ></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className="ri-map-pin-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">公司地址</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{siteConfig.address}</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className="ri-mail-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                  <p className="text-xs text-foreground-600">{siteConfig.email}</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className="ri-phone-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">客服电话</h3>
                  <p className="text-xs text-foreground-600">{siteConfig.phone}</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}